// Generated by GitHub Copilot
import { ApiError, apiErrorKey, isServiceUnavailable } from '#core/api/Api.ts'
import AuthenticatorAppApi from '#core/api/auth/AuthenticatorAppApi.ts'
import MfaApi from '#core/api/auth/MfaApi.ts'
import type AuthenticatorAppEnrollment from '#core/entities/auth/mfa/AuthenticatorAppEnrollment.ts'
import type MfaFactorList from '#core/entities/auth/mfa/MfaFactorList.ts'
import User from '#core/entities/auth/users/User.ts'
import { State } from '#core/state/State.ts'
import type { UserState } from '#core/state/UserState.ts'

const mfaApi = new MfaApi()
const authenticatorAppApi = new AuthenticatorAppApi()

/** A setup failure the panel explains in its own words. */
export type AuthenticatorNotice = 'invalidCode' | 'locked' | 'notAvailable' | 'setupExpired' | 'failed'

function httpStatus(e: unknown): number | undefined {
  return e instanceof ApiError ? e.response?.status : undefined
}

/** Drives the authenticator-app section: the account's factors, the setup flow and removal. */
export class AuthenticatorAppState extends State<AuthenticatorAppState> {
  factors: MfaFactorList | null = null
  loadFailed = false
  /** The pending app while the user is setting it up; null otherwise. */
  enrollment: AuthenticatorAppEnrollment | null = null
  code = ''
  validated = false
  busy = false
  notice: AuthenticatorNotice | null = null
  /** Set once an app is confirmed during this visit: the names of the companies that confirmation unlocked. */
  unlockedCompanies: string[] | null = null

  constructor(key: string | undefined, storage: Storage | null) {
    super(key, storage)
    void this.load()
  }

  async load(): Promise<void> {
    this.reactTo.loadFailed = false
    try {
      this.reactTo.factors = await mfaApi.listFactors()
    } catch {
      this.reactTo.loadFailed = true
    }
  }

  /** Creates a pending app and shows its QR code and secret. */
  async startSetup(): Promise<void> {
    this.#begin()
    try {
      this.reactTo.enrollment = await authenticatorAppApi.enroll()
      this.reactTo.code = ''
      this.reactTo.validated = false
      this.reactTo.unlockedCompanies = null
    } catch (e) {
      if (httpStatus(e) === 409) await this.load()
      else this.reactTo.notice = apiErrorKey(e) === 'not_configured' || isServiceUnavailable(e) ? 'notAvailable' : 'failed'
    } finally {
      this.reactTo.busy = false
    }
  }

  cancelSetup(): void {
    this.reactTo.enrollment = null
    this.reactTo.code = ''
    this.reactTo.validated = false
    this.reactTo.notice = null
  }

  /**
   * Confirms the pending app with the entered code. The session it returns counts as MFA, so the user is
   * re-read to unlock the companies that require a code in place.
   */
  async confirm(userState: UserState): Promise<void> {
    const lockedBefore = User.memberCompanies(userState.user).filter((company) => company.lockReasons.includes('mfa'))
    this.#begin()
    try {
      await authenticatorAppApi.confirm(this.code)
    } catch (e) {
      await this.#failConfirm(e)
      this.reactTo.busy = false
      return
    }
    // The app is confirmed whatever happens next; a failed re-read only leaves the locks stale until the next one.
    await userState.reloadUser().catch(() => undefined)
    const stillLocked = new Set(
      User.memberCompanies(userState.user).filter((company) => company.lockReasons.includes('mfa')).map((company) => company.id.toLowerCase()),
    )
    this.reactTo.unlockedCompanies = lockedBefore.filter((company) => !stillLocked.has(company.id.toLowerCase())).map((company) => company.name)
    this.reactTo.enrollment = null
    this.reactTo.code = ''
    await this.load()
    this.reactTo.busy = false
  }

  /** Removes the app; throws when the backend refuses, so the confirm dialog can say so. */
  async remove(): Promise<void> {
    try {
      await authenticatorAppApi.remove()
    } catch (e) {
      if (httpStatus(e) !== 404) throw e
    }
    this.reactTo.unlockedCompanies = null
    await this.load()
  }

  #begin(): void {
    this.reactTo.busy = true
    this.reactTo.notice = null
  }

  async #failConfirm(e: unknown): Promise<void> {
    const key = apiErrorKey(e)
    if (key === 'mfa_invalid_code') {
      this.reactTo.code = ''
      this.reactTo.validated = false
      this.reactTo.notice = 'invalidCode'
    } else if (key === 'mfa_locked') {
      this.reactTo.notice = 'locked'
    } else if (key === 'not_configured' || isServiceUnavailable(e)) {
      this.reactTo.notice = 'notAvailable'
    } else if (httpStatus(e) === 404 || httpStatus(e) === 409) {
      // Nothing pending any more (replaced or removed elsewhere), or already confirmed elsewhere.
      this.reactTo.enrollment = null
      this.reactTo.notice = httpStatus(e) === 404 ? 'setupExpired' : null
      await this.load()
    } else {
      this.reactTo.notice = 'failed'
    }
  }
}
